import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form } from "@/app/global-components/form";
import { Separator } from "@/app/global-components/separator";
import ProfileSubmitView from "./profile-submit-view";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";
import { RootState } from "@/store/type";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ProfileSchema } from "@/schema";
import useProfile from "@/hooks/use-profile";
import ProfileFieldView from "./profile-field-view";
import { useDispatch } from "react-redux";
import { getMyCompany } from "@/store/authSlice";

interface Region {
  region_id: number;
  name_ar: string;
}

interface City {
  city_id: number;
  region_id: number;
  name_ar: string;
}

interface Option {
  value: string;
  label: string;
}

const EditProfileFormView = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const userId = authUser.id;
  const { updateUser, getUserProfile, isSaveLoading, error } = useProfile();
  const [regions, setRegions] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);
  const [isNewUser, setIsNewUser] = useState(false);
  const dispatch = useDispatch();

  const form = useForm<z.infer<typeof ProfileSchema>>({
    resolver: zodResolver(ProfileSchema),
    defaultValues: {
      id: null,
      nickName: "",
      email: "",
      address: "",
      phoneNumber: "",
      role: "Client",
      gender: null,
      accountType: null,
      region: "",
      city: "",
      identityType: "Citizen",
      identityNo: "",
      residenceNo: "",
    },
  });
  useEffect(() => {
    setRegions(
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      }))
    );
  }, []);

  useEffect(() => {
    const selectedRegion = form.watch("region");
    if (selectedRegion) {
      const filteredCities = citiesData
        .filter((city: City) => city.region_id === parseInt(selectedRegion))
        .map((city: City) => ({
          value: city?.city_id.toString(),
          label: city.name_ar,
        }));
      setCities(filteredCities);
    } else {
      setCities([]);
    }
  }, [form.watch("region")]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (userId) {
        try {
          const userData = await getUserProfile();
          if (userData) {
            dispatch(getMyCompany(userData.data.company));
            let dataUser = {
              ...userData.data.user,
              region: isEmpty(userData.data.user.region)
                ? myCompany?.region
                : userData.data.user.region,
              city: isEmpty(userData.data.user?.city)
                ? myCompany?.city
                : userData.data.user?.city,
            };
            form.reset(dataUser);
            form.setValue("identityType", "Citizen");
            setIsNewUser(false);
          } else {
            setIsNewUser(true);
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          setIsNewUser(true);
        }
      }
    };

    fetchUserProfile();
  }, []);

  const onSubmit = async (values: z.infer<typeof ProfileSchema>) => {
    try {
      const submissionData = {
        ...values,
        identityNo:
          values.identityType === "Citizen" ? values.identityNo : null,
        residenceNo:
          values.identityType === "Resident" ? values.residenceNo : null,
      };
      await updateUser(submissionData);
    } catch (err) {
      console.error("Error during profile submission:", err);
    }
  };
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="bg-white space-y-4 border p-5 rounded-2xl"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ProfileFieldView
            name="nickName"
            label={t("profile.fields.nickName")}
            placeholder={t("profile.placeholders.enterNickName")}
            formControl={form.control}
          />
          <ProfileFieldView
            name="email"
            label={t("profile.fields.email")}
            placeholder={t("profile.placeholders.enterEmail")}
            formControl={form.control}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ProfileFieldView
            name="phoneNumber"
            label={t("profile.fields.phoneNumber")}
            placeholder={t("profile.placeholders.enterPhoneNumber")}
            formControl={form.control}
          />
          <ProfileFieldView
            name="address"
            label={t("company.fields.address")}
            placeholder={t("profile.placeholders.enterAddress")}
            formControl={form.control}
          />
        </div>
        <Separator className="w-full bg-primary-gray h-[2px]" />
        <div className="flex flex-col justify-between sm:flex-row xs:gap-5 py-2 w-full">
          <section className="sm:w-1/2 w-full flex flex-col">
            <ProfileFieldView
              placeholder="test"
              name="region"
              label={t("company.fields.region")}
              inputType="combobox"
              options={regions}
              formControl={form.control}
            />
          </section>
          <section className="flex flex-col sm:w-1/2 w-full">
            <ProfileFieldView
              name="city"
              label={t("company.fields.city")}
              placeholder={t("company.placeholders.selectCity")}
              inputType="combobox"
              options={cities}
              formControl={form.control}
            />
          </section>
        </div>
        <Separator className="w-full bg-primary-gray h-[2px]" />
        <div className="flex flex-col lg:grid lg:grid-cols-4">
          <section className="flex-1 mt-2">
            <ProfileFieldView
              name="gender"
              label={t("profile.fields.gender")}
              placeholder={t("profile.placeholders.selectGender")}
              inputType="radio"
              options={[
                { value: "Male", label: t("profile.options.gender.male") },
                { value: "Female", label: t("profile.options.gender.female") },
              ]}
              formControl={form.control}
            />
          </section>
          <section className="flex-1 mt-2">
            <ProfileFieldView
              name="accountType"
              label={t("profile.fields.accountType")}
              placeholder={t("profile.placeholders.selectAccountType")}
              inputType="radio"
              options={[
                {
                  value: "Individual",
                  label: t("profile.options.accountType.individual"),
                },
                {
                  value: "Establishment",
                  label: t("profile.options.accountType.establishment"),
                },
              ]}
              formControl={form.control}
            />
          </section>
        </div>
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        <div className="flex justify-end">
          <ProfileSubmitView isLoading={isSaveLoading} isNewUser={isNewUser} />
        </div>
      </form>
    </Form>
  );
};

export default EditProfileFormView;
